import type { Role } from "@/lib/toolsData";

interface RoleContextCardProps {
  role: Role;
}

const ROLE_ICONS: Record<Role, string> = {
  Atlas: "🧠",
  Comet: "⚡",
  Manus: "✍️",
};

const ROLE_LABELS: Record<Role, string> = {
  Atlas: "Think",
  Comet: "Clarify",
  Manus: "Create",
};

const ROLE_CONTEXTS: Record<Role, { title: string; description: string; examples: string[] }> = {
  Atlas: {
    title: "Think: Research, Plan & Organize",
    description: "Use these tools when you need to research a topic, plan a project, structure ideas, or work through complex problems.",
    examples: [
      "Research a new field or topic",
      "Plan a project or strategy",
      "Structure ideas and organize thoughts",
      "Analyze and understand complex concepts",
    ],
  },
  Comet: {
    title: "Clarify: Get Fast Answers & Summaries",
    description: "Use these tools when you need fast answers, summaries, comparisons, or quick information without sifting through search results.",
    examples: [
      "Get a quick summary of something",
      "Compare options side-by-side",
      "Find information fast",
      "Answer a specific question",
    ],
  },
  Manus: {
    title: "Create: Write, Design & Produce",
    description: "Use these tools when you're writing content, generating images, creating designs, or automating tasks.",
    examples: [
      "Write blog posts, emails, or scripts",
      "Generate images or designs",
      "Create videos or audio content",
      "Automate repetitive tasks",
    ],
  },
};

export default function RoleContextCard({ role }: RoleContextCardProps) {
  const context = ROLE_CONTEXTS[role];
  const label = ROLE_LABELS[role];

  return (
    <div className="rounded-lg border border-border bg-card p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="text-3xl">{ROLE_ICONS[role]}</div>
        <div className="flex-1">
          <h3 className="font-['Fraunces'] text-lg font-semibold text-foreground mb-2">
            {context.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            {context.description}
          </p>
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-2">Use {label} tools when you need to:</p>
            <ul className="space-y-1">
              {context.examples.map((example) => (
                <li key={example} className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{example}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
